import React from 'react';

import { HStack, Icon, useColorModeValue, Text, useDisclosure, Stack, Collapse, Button, Box } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router';

import { AccessResourceKey } from 'hooks/data/files/types';
import useAccess from 'hooks/useAccess';
import useTourAvailable from 'hooks/useTourAvailable';
import { checkAccessToResource } from 'utilities/auth/access';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { NavLink } from 'utilities/routing/types';

import { useLicensedTour } from '../LicensedTour';
import { TOUR_VERSION } from '../LicensedTour/constants';
import { useUnlicensedTour } from '../UnlicensedTour';

import { doPathsMatch } from './utils';

type Props = NavLink & {
  onClick?: (path: string) => void;
  tourTarget?: string;
  chevronTourTarget?: string;
};

const SideBarLink = ({
  label,
  path = '',
  extraMatchingPaths = [],
  icon,
  isExternal,
  onClick,
  exactMatch,
  subItems,
  tourTarget,
  chevronTourTarget,
}: Props) => {
  const isActiveBG = useColorModeValue('blackAlpha.50', 'whiteAlpha.200');
  const isActiveHover = useColorModeValue('blackAlpha.100', 'whiteAlpha.100');
  const isActiveColor = useColorModeValue('green.500', 'green.200');
  const isInactiveColor = useColorModeValue('gray.500', 'gray.300');

  const { isOpen, onToggle } = useDisclosure();

  const { data: accessData } = useAccess();
  const { startTour: startLicensedTour } = useLicensedTour();
  const { startTour: startUnlicensedTour } = useUnlicensedTour();
  const hasTourVersionAccess = checkAccessToResource(accessData, AccessResourceKey.SCORES, TOUR_VERSION);
  const isTourAvailable = useTourAvailable();

  const isActive = doPathsMatch({
    path,
    extraPaths: extraMatchingPaths,
    url: window.location.pathname,
    exactMatch,
  });

  const handleDropdownToggle = () => {
    if (label === 'Legal & Privacy') {
      mixpanel.track(isOpen ? MIXPANEL_EVENTS.LEGAL_PRIVACY_DROPDOWN_CLOSED : MIXPANEL_EVENTS.LEGAL_PRIVACY_DROPDOWN_OPENED);
    }
    onToggle();
  };

  const handleSubItemClick = (subItem: NavLink, onClick?: (path: string) => void) => {
    if (label === 'Legal & Privacy') {
      mixpanel.track(
        subItem.label === 'Privacy Policy'
          ? MIXPANEL_EVENTS.LEGAL_PRIVACY_PRIVACY_POLICY_CLICKED
          : MIXPANEL_EVENTS.LEGAL_PRIVACY_EULA_CLICKED
      );
    }
    if (subItem.path && subItem.path !== '#cookie-preferences') {
      onClick?.(subItem.path);
    }
  };

  return (
    <Stack>
      <HStack
        py={2}
        px={3}
        spacing={3}
        background={subItems && isOpen ? isActiveBG : isActive ? isActiveBG : 'auto'}
        _hover={!isActive ? { bg: isActiveHover } : {}}
        color={subItems && isOpen ? isActiveColor : isActive ? isActiveColor : isInactiveColor}
        as={RouterLink}
        to={path}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        borderRadius={'lg'}
        onClick={(event) => {
          if (subItems) {
            event.preventDefault();
            handleDropdownToggle();
            return;
          }
          onClick?.(path);
        }}
        fontWeight={subItems && isOpen ? 'bold' : isActive ? 'bold' : 'normal'}
        transition={'all 0.1s ease-in-out'}
        justifyContent={'space-between'}
        data-tour-target={tourTarget}
      >
        <HStack spacing={3}>
          <Icon as={icon} fontSize={'xl'} />
          <Text>{label}</Text>
        </HStack>
        {subItems ? (
          <Box data-tour-target={chevronTourTarget}>
            <Icon as={isOpen ? FiChevronUp : FiChevronDown} />
          </Box>
        ) : (
          isExternal && <Icon as={FiExternalLink} />
        )}
      </HStack>
      {subItems && (
        <Collapse in={isOpen}>
          <Stack>
            {subItems.map((subItem) => {
              if (subItem.label === 'Product Tour' && !isTourAvailable) return null;
              return (
                <Stack px={2} key={subItem.path} fontSize={'sm'}>
                  {subItem.label === 'Cookie Preferences' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      fontWeight="normal"
                      color={isInactiveColor}
                      leftIcon={<Icon as={subItem.icon} fontSize="xl" mr={1} />}
                      _hover={{ bg: isActiveHover }}
                      py={2}
                      px={3}
                      h="37px"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        mixpanel.track(MIXPANEL_EVENTS.LEGAL_PRIVACY_COOKIE_PREFERENCES_CLICKED);

                        const cookiebot = window.Cookiebot;
                        if (!cookiebot) return;
                        if (cookiebot.hasResponse || cookiebot.consented || cookiebot.declined) {
                          const widget = document.querySelector('#CookiebotWidget') as HTMLElement;
                          if (widget) {
                            widget.classList.add('show-widget');
                            const isOpen = widget.classList.contains('CookiebotWidget-open');
                            if (!isOpen) {
                              const widgetButton = widget.querySelector('button.CookiebotWidget-logo') as HTMLElement;
                              if (widgetButton) widgetButton.click();
                            }
                          } else {
                            cookiebot.renew();
                          }
                        } else {
                          cookiebot.show();
                        }
                      }}
                      aria-label={subItem.label}
                      w="100%"
                      justifyContent="flex-start"
                    >
                      {subItem.label}
                    </Button>
                  ) : subItem.label === 'Product Tour' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      fontWeight="normal"
                      color={isInactiveColor}
                      leftIcon={<Icon as={subItem.icon} fontSize="xl" mr={1} />}
                      _hover={{ bg: isActiveHover }}
                      py={2}
                      px={3}
                      h="37px"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        mixpanel.track(MIXPANEL_EVENTS.TOUR_OPENED, {
                          tour_type: hasTourVersionAccess ? 'licensed' : 'unlicensed',
                          source: 'sidebar',
                        });

                        if (hasTourVersionAccess) {
                          startLicensedTour('sidebar', true);
                        } else {
                          startUnlicensedTour('sidebar', true);
                        }
                      }}
                      aria-label={subItem.label}
                      w="100%"
                      justifyContent="flex-start"
                    >
                      {subItem.label}
                    </Button>
                  ) : (
                    <SideBarLink key={subItem.path} {...subItem} onClick={() => handleSubItemClick(subItem, onClick)} />
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
};

export default SideBarLink;
